import React, { createContext, useContext, useCallback } from 'react';

// ─── Translation dictionaries ────────────────────────────────────────────
const translations = {
  en: {
    // ── General / Shared ──
    settings: 'Settings',
    howToPlay: 'How to Play',
    feedback: 'Feedback',
    logout: 'Logout',
    people: 'People',
    close: 'Close',
    back: 'Back',
    join: 'Join',
    leave: 'Leave',
    send: 'Send',
    loading: 'Loading…',
    retry: 'Retry',
    submitting: 'Submitting…',
    yes: 'Yes',
    no: 'No',
    appErrorTitle: 'Something went wrong',
    appErrorBody: 'The app encountered an unexpected error. You can go back to the lobby or reload the page.',
    appErrorBackToLobby: 'Back to Lobby',
    appErrorReload: 'Reload Page',

    // ── Login Screen ──
    kosteczki: 'Kosteczki',
    strategyBoardGame: 'Strategy board game',
    online: 'online',
    loginTagline: 'Score points by building the largest connected islands of your colour on a shared, ever-growing board.',
    featureMultiplayer: 'Online Multiplayer',
    featureMultiplayerDesc: 'Play with your friends in real-time. 5 to 8 players per game.',
    featureElo: 'Elo Ranking System',
    featureEloDesc: 'Compete on a global leaderboard and earn your rank.',
    featureAI: 'AI Opponents',
    featureAIDesc: 'Play solo against bots. We will fill any empty seats automatically.',
    featureUnique: 'Each game is different',
    featureUniqueDesc: 'Choose map generation algorithm and enjoy a unique experience each time.',
    howToPlayQuestion: 'How to play?',
    watchDemo: '▶ Watch a demo game',
    playGuidedDemo: '✨ Play guided demo',
    watchDemoHint: 'Not sure yet? Watch a real game replay above — no account needed.',
    signIn: 'Sign in',
    createAccount: 'Create account',
    resetYourPassword: 'Reset your password',
    resetPasswordDesc: "We'll send a reset link to your email address.",
    createYourAccount: 'Create your account',
    welcomeBack: 'Welcome back',
    emailPlaceholder: 'Email address',
    passwordPlaceholder: 'Password',
    usernamePlaceholder: 'Username',
    sendResetEmail: 'Send reset email',
    backToSignIn: 'Back to sign in',
    forgotPassword: 'Forgot your password?',
    orDivider: 'or',
    guestPlay: 'Play as Guest',
    guestPlayDesc: 'Try the game without creating an account. Progress saved for 24h.',
    guestUsername: 'Choose a username',
    guestStartPlaying: 'Start Playing',
    guestBanner: 'Guest account — expires in {time}',
    guestUpgrade: 'Save Your Progress',
    guestUpgradeDesc: 'You are currently playing on a temporary guest account. Create a free full account to keep your progress forever — no hidden fees, no payments.',
    guestUpgradeEmail: 'Email address',
    guestUpgradePassword: 'Password (min. 6 characters)',
    guestUpgradeButton: 'Create Free Account',
    guestUpgradeSuccess: 'Confirmation email sent! Check your inbox to complete the upgrade.',
    guestUpgradeConfirmed: 'Account upgraded! Your progress has been preserved.',
    guestExpired: 'Your guest session has expired. Please create an account to continue playing.',
    errUsernameExists: 'This username is already taken.',
    footerHobby: 'Kosteczki is a free hobby project — no payments, no ads, no data selling.',
    footerUpdated: 'Last updated: Mar 2026',
    footerContact: 'Contact:',

    // ── Reset Password ──
    setNewPassword: 'Set new password',
    newPasswordDesc: 'Enter a new password for your account.',
    newPasswordPlaceholder: 'New password (min. 6 characters)',
    updatePassword: 'Update password',

    // ── Verify Email ──
    confirmEmail: 'Confirm your email',
    confirmEmailDesc: 'We sent a confirmation link to:',
    confirmEmailInstructions: "Click the link in the email to activate your account. You won't be able to access the game until you do.",
    emailConfirmed: 'Email confirmed! Please log in.',
    emailResent: 'Confirmation email has been resent!',
    checkAgain: "✅ I've confirmed — check again",
    resendEmail: '📨 Resend email',
    logOut: '🚪 Log out',
    checkSpam: "Can't find the email? Check your spam folder.",

    // ── Rejoin Screen ──
    welcomeBackUser: 'Welcome back, {user}!',
    activeGamesToRejoin: 'Active Rooms',
    roomLabel: 'Room: {code}',
    playersInGame: '{count} players in game',
    playersInLobby: '{count}/{total} players in lobby',
    statusWaiting: 'Waiting',
    roomStatusWaiting: 'Waiting',
    roomStatusPlaying: 'In Game',
    rejoin: '↩️ Rejoin',
    returnToLobby: '↩️ Return to Lobby',
    orStartNew: 'Or start a new game:',
    createNewOrBrowse: '➕ Create New Game or Browse',
    rejoinOrLeaveHint: 'You have active rooms. Please rejoin or leave them before starting a new game.',

    // ── Lobby Screen ──
    welcomeUser: 'Welcome, {user}!',
    openRooms: 'Open Rooms',
    watchRooms: 'Watch Rooms',
    noRoomsAvailable: 'No available rooms at the moment. Create one to get started!',
    codeLabel: 'Code: {code}',
    hostLabel: 'Host: {host}',
    playersSlots: '{current}/{total} players',
    mapLabel: 'Map: {algorithm}',
    spotsLeft: '{count} spot(s) left',
    noWatchableRooms: 'No active games to watch right now. Check back soon!',
    liveBadge: 'Live',
    playersCount: '{count} players',
    watchButton: 'Watch',
    haveACode: 'Have a code?',
    hideCodeEntry: 'Hide code entry',
    enterRoomCode: 'Enter room code',
    joinArrow: 'Join Room',
    leaderboard: 'Leaderboard',
    leaderboardQualify: '5 games are required to qualify for the leaderboard.',
    leaderboardPlayers: 'Players',
    leaderboardBots: 'Bots',
    leaderboardFriendsOnly: 'Friends only',
    leaderboardShowing: 'Showing {start}-{end} of {total}',
    leaderboardPrev: 'Prev',
    leaderboardNext: 'Next',
    leaderboardNoFriends: 'No qualified friends yet',
    leaderboardTrendUp: 'Rising {delta}',
    leaderboardTrendDown: 'Falling {delta}',
    leaderboardTrendFlat: 'No recent change',
    leaderboardPendingBadge: 'Pending',
    noGamesPlayed: 'No games played yet',
    needMoreGames: 'Need {count} more game(s)',
    createARoom: 'Create a Room',
    createRoomDesc: 'Create a private room and choose the players, rules, and flow of the match.',
    createRoomButton: 'Create Room',
    globalChatInline: 'Global Chat',

    // ── Create Room Screen ──
    createARoomHeading: 'Create a Room',
    backButton: '← Back',
    roomSettings: 'Room Settings',
    numberOfPlayers: 'Number of Players',
    mapType: 'Map Type',
    corridors: 'Corridors',
    corridorsDesc: 'Narrow paths, tactical blocking',
    compact: 'Compact',
    compactDesc: 'Circular growth, close quarters',
    sprawling: 'Sprawling',
    sprawlingDesc: 'Open space, strategic positioning',
    roomVisibility: 'Room Visibility',
    privateRoom: 'Private Room',
    publicRoom: 'Public Room',
    privateRoomDesc: 'Hidden from lobby — share the code to invite players',
    publicRoomDesc: 'Visible in the lobby — anyone can join',
    swapLimitPerPlayerLabel: 'Swaps per Player',
    swapLimitPerPlayerDesc: 'Choose one room preset: no limit or 5 swaps per player.',
    swapLimitInputPlaceholder: 'No limit',
    swapLimitUnlimited: 'No limit',
    swapLimitPresetFive: '5 swaps/player',
    creatingRoom: 'Creating…',
    createOnlineRoom: 'Create Online Room',

    // ── Room Lobby Screen ──
    youAreHost: 'You are the host',
    hostIs: 'Host: {host}',
    privateBadge: 'Private',
    copyCode: 'Copy Code',
    copiedCode: 'Copied!',
    playersOf: 'Players ({current}/{total})',
    avgElo: 'Avg ELO: {elo}',
    eloMultiplier: '{count}-player bonus: +{bonus}% ELO',
    hostBadge: 'HOST',
    aiSuffix: '(AI)',
    botPersonaTooltip: '{persona} is the heuristic base. Final style also depends on the bot nickname seed.',
    ready: '✓ Ready',
    waiting: 'Waiting',
    emptySlot: 'Empty slot',
    readyButton: '✓ Ready',
    notReadyButton: 'Not Ready',
    readyCooldownHint: 'You can change readiness again in {seconds}s.',
    fillAI: 'Add Bots',
    addingAI: 'Adding bots…',
    startGame: 'Start',
    startingGame: 'Starting…',
    leaveRoom: 'Leave room',
    kick: 'Kick',
    kickPlayer: 'Kick {user} from room',
    kickedNotifMsg: '🚪 {user} was removed from the room',

    gameTitle: 'Kosteczki Game ({code})',
    stopWatching: 'Stop Watching',
    stopWatchingShort: 'Stop',
    sitIn: 'Sit In',
    sitInNoticeTitle: 'AI took over your seat',
    sitInNoticeBody: 'Your seat is temporarily being played by AI because you went inactive. Every AI move adds a final ELO penalty for the part of the game it covered. Jump back in to stop the penalty from growing.',
    sitInNoticeRatingHint: 'So far AI has played {aiMoves} of {totalMoves} move(s) on your seat: {gainPenalty}% less gain and {lossPenalty}% more loss at match end.',
    sitInNoticeRatingHintIdle: 'AI has not taken any of your turns yet, so the penalty is still 0%. Every future AI move will reduce gains and increase losses at match end.',
    leaverLabel: 'AI cover',
    leaverPenaltyAria: 'Show AI takeover rating details',
    leaverPenaltyTitle: 'AI takeover ELO adjustment',
    leaverPenaltyMoves: 'AI played {aiMoves} of {totalMoves} move(s) on this seat ({percent}%).',
    leaverPenaltyLossBreakdown: '{base} pts + {penalty}% AI penalty = total {total} pts',
    leaverPenaltyGainBreakdown: '{base} pts - {penalty}% AI reduction = total {total} pts',
    leaveAI: 'Leave (AI takes over)',
    leaveAIShort: 'Leave',
    leaveAITooltip: "You'll be replaced by AI",
    stopWatchingTooltip: 'Stop watching this game',
    sitInTooltip: 'Take back your seat and continue playing',
    yourTurn: 'Your Turn!',
    aiPlayingYourSeatTurn: 'AI is playing your seat',
    playerTurn: "{player}'s Turn",
    turnBadge: '▶ TURN',
    gameRuleColor: 'Your color',
    gameRuleMode: 'Mode',
    gameModeClassic: 'Classic',
    gameModeSwapCap: '{count} swaps',
    unstuckBotButton: 'Unstuck Bot',
    unstuckBotTooltip: 'Dump diagnostics for {player} to the console and force a retry',
    unstuckBotDisabledTooltip: 'Available only during an AI turn',
    unstuckBotLabel: 'unstuck',
    swapRemainingSelf: 'Your swaps',
    swapRemainingShort: 'Swaps',
    swapButton: 'SWAP',
    swapHint: 'Exchange your entire hand with this player. Best when their hand has more of your colour. If the last N moves were swaps, where N is the room size, the next player must place a marker.',
    swapUnavailableHint: 'Swap is unavailable this turn. You may have reached the room swap limit, or the last N moves were swaps so you must place a marker now.',
    swapEmptyPoolHint: 'The draw bag is empty, so swap is unavailable.',
    firstMoveHint: '👆 Select a tile from your hand (right panel), then click a white cell on the board',
    placeMarkerHintCompact: 'Pick a tile, then tap a white cell',
    selectedMarkerLabel: 'Selected',
    placementPreview: 'Preview {row}, {col}',
    cellMarkerPickerTitle: 'Choose a tile for this cell',
    cellMarkerPickerHint: 'Select a tile first. You can still cancel before placing it.',
    cellMarkerPickerMarker: 'Choose tile {index}',
    cellMarkerPickerConfirm: 'Place here',
    cellMarkerPickerCancel: 'Cancel',
    timerExpired: 'Time is up. AI takes over this turn.',
    lastRoundBanner: '🏁 Endgame! {count} moves left',
    scoreGain: '+{pts} pts',
    profileButton: 'PROFILE',
    leftToDraw: '🎒 Left to draw',
    moveHistory: 'Move History ({count})',
    noMovesYet: 'No moves yet',
    movePlaced: '{player} → ({row}, {col})',
    moveSwapped: '{player} ↔ {target}',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomReset: 'Reset zoom',

    // ── Game Reactions ──
    reactionNice: 'Nice!',
    reactionHot: 'Hot!',
    reactionWow: 'Wow!',
    reactionBlock: 'Block!',
    reactionHaha: 'Haha',
    reactionOhNo: 'Oh no!',

    // ── Game End Modal ──
    gameOver: 'Game Over',
    gameResults: 'Match Results',
    place: 'Place',
    player: 'Player',
    pts: 'Pts',
    elo: 'ELO',
    exAequo: 'ex aequo',
    exAequoWinner: 'Ex aequo:',
    winner: 'Winner:',
    ptsScore: '— {score} pts',
    playersLabel: '{count} players',
    rematchBtn: 'Play again',
    rematchCreated: 'Rematch room created: {code}',
    rematchJoined: 'Joined the rematch room: {code}',
    watchReplayBtn: 'Watch replay',
    backToMenu: 'Back to Menu',
    menuLabel: 'Menu',
    durationLabel: 'Duration',

    // ── Settings Modal ──
    settingsHeading: '⚙️ Settings',
    languageHeading: 'Language / Język',
    themeHeading: 'Color Theme',
    themeDescription: 'Switch the background, cards and main accents across the whole app.',
    themeGroupPastel: 'Pastel',
    themeGroupVivid: 'Vivid',
    themeGroupNeutral: 'Neutral',
    themeNebula: 'Nebula',
      themeNebulaDesc: 'Deep navy and violet base with crisp blue highlights.',
    themeEmber: 'Solar Ember',
      themeEmberDesc: 'Dark copper and burnt orange tones with a warm glow.',
    themeLagoon: 'Lagoon',
      themeLagoonDesc: 'Petrol blue and teal tones with clean aquatic highlights.',
    themeForest: 'Forest',
      themeForestDesc: 'Pine green palette with moss accents and a calmer finish.',
    themeAurora: 'Aurora Pulse',
      themeAuroraDesc: 'Dark teal horizon with mint and lime highlights that feel vivid but clean.',
    themeSunset: 'Sunset Pop',
      themeSunsetDesc: 'Plum, coral and golden amber blended into a warm neon dusk.',
    themeCitrus: 'Citrus Punch',
      themeCitrusDesc: 'Lime and tangerine accents over a dark olive base with playful energy.',
    themeGraphite: 'Graphite',
      themeGraphiteDesc: 'Near-black base with smoke gray layers and silver accents.',
    themeRose: 'Rose Glow',
      themeRoseDesc: 'Deep berry background with soft pink layers and rose highlights.',
    themeDawn: 'Pastel Dawn',
      themeDawnDesc: 'Dusty lavender and apricot accents over a calm evening-blue base.',
    themeSage: 'Soft Sage',
      themeSageDesc: 'Muted eucalyptus greens and warm sand tones for a softer, quieter look.',
    themeLilac: 'Lilac Mist',
      themeLilacDesc: 'Smoky plum, powder blue and lavender accents in a gentle pastel mix.',
    themePeach: 'Peach Veil',
      themePeachDesc: 'Powder peach and mauve accents with a soft, late-evening pastel glow.',
    themeSeaglass: 'Sea Glass',
      themeSeaglassDesc: 'Soft aqua and shell pink layers with a calm, airy pastel finish.',
    themeLaser: 'Laser Grid',
      themeLaserDesc: 'Electric blue and neon rose over a deep ink base with a crisp arcade feel.',
    themeMoonstone: 'Moonstone',
      themeMoonstoneDesc: 'Slate blue-gray layers with quiet silver highlights and a cool neutral feel.',
    themeSandstone: 'Sandstone',
      themeSandstoneDesc: 'Warm stone and beige tones for a soft neutral palette without extra contrast.',
    themeTruffle: 'Truffle',
      themeTruffleDesc: 'Muted cocoa, taupe and sage tones for a grounded neutral palette with warmth.',
    accessibilityHeading: 'Accessibility & Display Options',
    boardView: 'Board View',
    view3d: '3D (Perspective)',
    view2d: "2D (Bird's Eye)",
    viewFancy3d: 'Full Fancy 3D',
    view3dDesc: '3D view with perspective effect - More immersive',
    view2dDesc: '2D view from above - Better for accessibility',
    viewFancy3dDesc: 'Cinematic mode: ocean, dense fog, build sites and evolving villages based on connected islands.',
    colorblindSymbols: 'Colorblind Symbols',
    symbolsOn: 'Symbols on',
    symbolsOff: 'Symbols off',
    symbolsOnDesc: 'Unique shapes (● ▲ ■ ◆ ★ …) are shown on markers and board cells for colorblind accessibility.',
    symbolsOffDesc: 'Symbols are hidden — markers are distinguished by color only.',
    gameReactionsLabel: 'Game Reactions',
    reactionsOn: 'Reactions on',
    reactionsOff: 'Reactions off',
    reactionsOnDesc: 'Quick emoji reactions (👍 🔥 🤯) are shown during online games.',
    reactionsOffDesc: 'Reactions are hidden — no floating emoji during games.',
    chatSoundLabel: 'Chat sound notifications',
    chatSoundOn: 'Sounds on',
    chatSoundOff: 'Sounds off',
    chatSoundOnDesc: 'Play a short sound for new hidden chat messages from other players.',
    chatSoundOffDesc: 'Chat stays silent — unread badges still work.',
    preferredColor: 'Preferred Color',
    preferredColorDesc: "Choose your preferred color in games. If taken by another player, you'll get the next available color.",
    colorN: 'Color {n}',
    closeSettings: 'Close Settings',
    openSettings: 'Open settings',

    // ── How to Play Modal ──
    htpTitle: 'How to Play',
    htpGoal: 'Goal',
    htpGoalText: 'Score the most points by building large connected groups (islands) of your color on a shared board. When all markers have been placed, the player with the highest total score wins.',
    htpSetup: 'Setup',
    htpSetupText1: 'Each player is assigned a unique color. At the start you receive 3 random markers — they can be any color, not necessarily yours.',
    htpSetupText2: 'The board starts with a single open cell in the center. The first player must place a marker there (or swap). Each placement opens the 4 directly adjacent cells, gradually revealing the map.',
    htpYourTurn: 'Your turn',
    htpYourTurnText: 'On each turn you must do one of the following. In online games, each human turn has a 20-second timer.',
    htpPlaceMarker: 'A. Place a marker',
    htpPlaceMarkerText: 'Tap / Drag (or click & drop) any marker from your hand onto an open (white) cell. Every closed cell directly adjacent to your placement opens up. You then draw a new marker from the shared pool.',
    htpSwapHands: 'B. Swap hands',
    htpSwapHandsText: "Exchange your entire hand with another player's hand. Available only when the draw pool is not empty. Rooms use one of two swap presets: Unlimited or 5 swaps per player for the whole match. If the last N moves were swaps, where N is the room size, the next player must place a marker. Use this to grab markers of your own color.",
    htpExpansion: 'Board expansion',
    htpExpansionText: 'When you place a marker, the 4 cells directly adjacent (up, down, left, right — never diagonally) change from closed to open. This is how the board grows.',
    htpGameStart: 'Game start',
    htpAfterFirst: 'After 1st move',
    htpAfterSecond: 'After 2nd move',
    htpExpansionNote: 'Notice: only the 4 direct neighbors open — diagonals stay closed. Plan your placements to expand toward areas that benefit your islands.',
    htpScoring: 'Scoring',
    htpScoringText: 'Your score is the sum of points from all your islands. Bigger islands are worth dramatically more:',
    htpCells: '{n} cell(s)',
    htpScoringFormula: 'Formula for 4+: 7 + (size − 4) × 2 — so a 7-cell island scores 13 points.',
    htpExample: 'Example',
    htpExampleText: 'Red has two separate islands — they score independently:',
    htpIslandA: 'Island A (3 cells) = 4 pts',
    htpIslandB: 'Island B (2 cells) = 1 pt',
    htpExampleNote: "Red's total: 4 + 1 = 5 points. If those 5 cells were all connected in one island instead, they would score 9 points — almost double. Keeping your cells connected pays off.",
    htpTips: 'Tips',
    htpTip1: 'Grow one big island rather than many small ones.',
    htpTip2: "You'll often place opponents' markers — put them where they help you (or hurt them).",
    htpTip3: "Use swaps to collect your own color when your hand is full of others'.",
    htpTip4: 'Expand the board toward areas that benefit your islands.',
    htpGotIt: 'Got it!',

    // ── Feedback Modal ──
    feedbackBoard: 'Feedback Board',
    feedbackBrowse: 'Browse',
    feedbackNew: '+ New',
    feedbackShow: 'Show:',
    feedbackOpen: 'Open',
    feedbackDoneClosed: 'Done / Declined',
    feedbackItems: '{count} item(s)',
    feedbackNoOpen: 'No open feedback items.',
    feedbackNoOpenHint: 'Fresh ideas and bug reports will show up here as soon as someone posts them.',
    feedbackNoDone: 'No done or declined items yet.',
    feedbackNoDoneHint: 'Completed or declined items move here after review.',
    feedbackLoadingTitle: 'Loading feedback board…',
    feedbackLoadingHint: 'Open ideas, fixes, and votes are being collected right now.',
    feedbackLoadError: 'Could not load the feedback list.',
    feedbackLoadErrorHint: 'The board is temporarily unavailable. Try again in a moment.',
    feedbackBug: 'Bug',
    feedbackIdea: 'Idea',
    feedbackDone: 'Done',
    feedbackDeclined: 'Declined',
    feedbackBy: 'by {user}',
    feedbackCantVoteOwn: 'Cannot vote on your own feedback',
    feedbackVoteLocked: 'Votes locked on completed items',
    feedbackRemoveVote: 'Remove vote',
    feedbackUpvote: 'Upvote',
    feedbackType: 'Type',
    feedbackSuggestion: 'Suggestion',
    feedbackBugReport: 'Bug Report',
    feedbackDescription: 'Description',
    feedbackBugPlaceholder: 'Describe the bug — what happened and what should have happened…',
    feedbackIdeaPlaceholder: 'Describe your suggestion or improvement idea…',
    feedbackCharCount: '{length}/500',
    feedbackSubmittingAs: 'Submitting as {user}',
    feedbackSubmit: 'Submit Feedback',

    // ── People / Friends Modal ──
    peopleHeading: 'People',
    tabOnline: 'Online',
    tabFriends: 'Friends',
    tabPending: 'Pending',
    statusPlaying: 'Playing',
    statusInRoom: 'In Room',
    statusActive: 'Active',
    statusIdle: 'Idle',
    noPlayersOnline: 'No players online',
    noPlayersOnlineHint: 'Create a room, invite someone, or check back in a moment.',
    peopleOnlineLoading: 'Checking who is online…',
    peopleOnlineLoadingHint: 'Presence is syncing now, so the room and game badges may appear in a second.',
    peopleOnlineError: 'Could not load the online list.',
    peopleOnlineErrorHint: 'Presence channel did not sync this time. Close the modal and try again.',
    profileLabel: 'PROFILE',
    viewProfile: "View {user}'s profile",
    moreCount: '+{count} more',
    noFriendsYet: 'No friends yet',
    noFriendsYetHint: "Open a player's profile to send the first request.",
    friendsPendingOnlyHint: 'You already have incoming requests above. Accept one to start your list.',
    peopleFriendsLoading: 'Loading your friends…',
    peopleFriendsLoadingHint: 'Requests, watch actions, and online statuses are being prepared now.',
    peopleFriendsError: 'Could not load your friends list.',
    peopleFriendsErrorHint: 'Friend requests are temporarily unavailable. Reopen the modal in a moment.',
    removeFriend: 'Remove friend',
    noPendingRequests: 'No pending requests',
    incomingRequests: 'Incoming requests',
    watchGame: 'Watch',
    accept: 'Accept',
    decline: 'Decline',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',

    // ── Profile Modal ──
    noEloHistory: 'No ELO history yet',
    profileLoading: 'Loading profile…',
    profileLoadingHint: 'Stats, badges, and recent replays are loading now.',
    errLoadProfile: 'Failed to load profile',
    profileLoadRetryHint: 'Try again in a moment or reopen the modal.',
    youTag: 'You',
    date: 'Date',
    rating: 'Rating',
    watchReplay: 'Watch replay',
    showMoreReplays: 'Show more',
    profileRecentGamesLoading: 'Loading recent games and replays…',
    profileRecentGamesHint: 'Replay actions will appear here as soon as match history is ready.',
    profileRecentGamesError: 'Recent games are temporarily unavailable.',
    profileRecentGamesErrorHint: 'The profile loaded, but replay history needs another try in a moment.',
    profileRecentGamesEmpty: 'No recent replays yet.',
    profileRecentGamesEmptyHint: 'Play a few online matches and this section will start filling up.',
    badgesHeading: 'Badges',
    badgeAlphaTester: 'Alpha Tester',
    badgeAlphaTesterDesc: 'Early supporter of Kosteczki',
    badgeWins: 'Victor',
    badgePerfect: 'Perfectionist',
    noBadges: 'No badges yet',
    gamesToNextTier: 'games to',
    winsToNextTier: 'wins to',
    perfectGamesToNextTier: 'perfect games to',
    tier_bronze: 'Bronze',
    tier_silver: 'Silver',
    tier_gold: 'Gold',
    tier_platinum: 'Platinum',
    tier_diamond: 'Diamond',
    tier_master: 'Master',
    aiBot: 'AI Bot',
    rankWorldwide: 'Rank #{rank} worldwide',
    addFriend: '➕ Add Friend',
    requestSent: 'Sent',
    acceptFriend: '✅ Accept',
    declineFriend: '✕ Decline',
    friendsStatus: '✓ Friends',
    unfriend: 'Unfriend',
    eloLabel: 'ELO',
    peak: 'Peak',
    games: 'Games',
    wins: 'Wins',
    losses: 'Losses',
    winRate: 'Win rate',
    goodGamesLabel: 'Good Games',
    eloHistory: 'ELO History',
    lastNGames: 'last {n} game(s)',
    recentGames: 'Recent games',
    botEloNote: 'AI ELO history will appear here after each recorded game.',
    noOnlineGames: 'No online games played yet',

    // ── Replay Modal ──
    noReplayData: 'No replay data available',
    replayInitial: 'Initial state — game start',
    replayPlaced: '{player} placed marker at ({row}, {col})',
    replaySwapped: '{player} swapped hands with {target}',
    demoReplayTitle: '🎬 Demo Game Replay',
    gameReplayTitle: '🎬 Game Replay',
    demoReplayDesc: 'See how a real game plays out — 5 players, strategy in action!',
    replayWinner: 'Winner:',
    showSymbolsCB: 'Show symbols (colorblind)',
    replayFirst: 'First (Home)',
    replayPrev: 'Previous (←)',
    replayPlayPause: 'Play/Pause (Space)',
    replayNext: 'Next (→)',
    replayLast: 'Last (End)',
    demoCoachTitle: 'Quick tutorial',
    demoCoachExplainToggle: 'Explain mode',
    demoCoachIntro: 'Goal: connect your own areas, because single tiles score 0 and larger islands become much more valuable.',
    demoCoachStep1: 'Step 1: Open the board from the center so future turns have room to grow.',
    demoCoachStep2: 'Step 2: Do not just place anywhere. Try to keep paths open toward your own color.',
    demoCoachStep3: 'Step 3: This move grows one connected island instead of creating another weak single tile.',
    demoCoachStep4: 'Step 4: Watch how the board opens around the placed marker. Tempo matters as much as color.',
    demoCoachStep5: 'Step 5: Swap is strongest when another player likely holds more of your color than you do.',
    demoCoachStep6: 'Step 6: Notice how defending your strongest island can be better than chasing every open cell.',
    demoCoachStep7: 'Step 7: Sometimes the best move is to prepare a bridge for the next two turns, not score immediately.',
    demoCoachStep8: 'Step 8: Keep pressure on central lanes - they decide who can still connect later.',
    demoCoachStep9: 'Step 9: From now on watch the full game at 2x and focus on shape, not single moves.',
    demoCoachStepMid: 'Watch each move and how territory control changes over time.',
    demoCoachGoal: 'Final lesson: the winner is the player who turns scattered tiles into bigger scoring islands most efficiently.',
    demoCoachContinue: 'Next guided move',
    demoCoachSkipCta: 'I get it - let me play',
    demoCoachReplayFromStart: 'Replay from start',
    demoCoachPickThenPlace: 'Pick a marker from your hand first, then click tile ({row}, {col}).',
    demoCoachClickTarget: 'Now it is your move: click tile ({row}, {col}).',
    demoCoachPickMarkerFirst: 'Pick a marker from your hand first.',
    demoCoachWrongPlayerMarker: 'Pick a marker from the active player hand.',
    demoCoachWrongMarker: 'Pick the highlighted marker for this move.',
    demoCoachWrongCell: 'Try the highlighted tile first to continue the tutorial.',
    demoCoachSwapAction: 'Great. Now trigger SWAP: {player} should swap hands with {target}.',
    demoCoachSwapPickTarget: 'Select swap target and confirm SWAP.',
    demoCoachSwapPickTargetFirst: 'Pick a swap target first.',
    demoCoachSwapPickOpponent: 'Pick another player as swap target.',
    demoCoachSwapWrongTarget: 'For this tutorial step, select the highlighted target player.',
    demoCoachSwapCta: 'Execute SWAP',
    demoCoachProgress: 'Tutorial progress: {current}/{total}',
    demoCoachFinishCta: 'Great. Close tutorial and play now',
    demoCoachSwapTitle: 'How swap works',
    demoCoachSwapBody: 'Swap exchanges your whole hand with another player. Use it when they likely hold more markers in your color.',
    demoCoachAutoplayLocked: 'Guided mode: autoplay unlocks after step 9',
    demoCoachAutoRun2x: 'Guide complete. Running the rest at 2x speed.',
    demoImpactPlaceTitle: 'Why this move matters',
    demoImpactPlaceChipTile: 'Marker placed',
    demoImpactPlaceChipOpen: '+{count} new cells opened',
    demoImpactPlaceChipIsland: 'Island size: {size}',
    demoImpactPlaceChipScore: 'Worth {points} pts at this size',
    demoImpactSwapTitle: 'Why this swap matters',
    demoImpactSwapChipActor: 'Actor: {player}',
    demoImpactSwapChipTarget: 'Target: {target}',
    demoImpactSwapChipGoal: 'Goal: improve future draws',
    demoBadgePlay: 'PLAY',
    demoBadgeOpen: 'OPEN',
    demoBadgeWinner: 'BEST',
    demoFinaleTitle: 'How scoring really works',
    demoFinaleBody: 'Single tiles are weak. Connecting your color into bigger islands rapidly increases your score, so shape and timing matter more than random expansion.',
    demoFinaleLegend: 'Island {size} = {points} pts',
    demoFinalePlayerSummary: 'Largest island: {largest} | Total score: {score} | Best islands: {islands}',

    // ── Chat Widget ──
    globalChat: 'Global Chat',
    chatButton: 'Chat',
    globalTab: 'Global',
    roomTab: 'Room',
    noRoomMessages: 'No room messages yet. Say hi!',
    noGlobalMessages: 'No messages yet. Start the conversation!',
    messageRoom: 'Message room...',
    messageEveryone: 'Message everyone...',
    chatCharCount: '{count}/{max}',
    emojiPicker: 'Emoji picker',

    // ── Online Players ──
    onlinePlayers: 'Online Players',

    // ── Error / Toast Messages ──
    errPasswordMin: 'Password must be at least 6 characters',
    errPasswordUpdated: 'Password updated successfully. You can now log in.',
    errEnterEmail: 'Please enter your email address',
    errResetSent: '✅ Password reset email sent! Check your inbox.',
    errEmailPassword: 'Please enter email and password',
    errEnterUsername: 'Please enter a username',
    errBotSuffix: 'Username cannot end with "*BOT" — that suffix is reserved for bots.',
    errUsernameFormat: 'Username must be 3–20 characters: letters, numbers and _ only (no spaces)',
    playerRejoined: 'rejoined the game',
    playerReplacedByAI: 'left the game and was replaced by AI',
    errProfileNotFound: 'Profile not found — please try again in a moment',
    errCreateRoom: 'Failed to create room.',
    errRoomGeneric: 'Something went wrong. Please try again.',
    errRoomNotFound: 'Room not found or no longer available.',
    errRoomFull: 'This room is already full.',
    errAlreadyInRoom: 'You are already in another active room.',
    errHostOnly: 'Only the host can perform this action.',
    errNotEnoughPlayers: 'Not enough players to start the game.',
    errGameAlreadyStarted: 'This game has already started.',
    errKickPlayer: 'Failed to remove this player.',
    errAddBot: 'Failed to add bots. Please try again.',
    errAuthGeneric: 'Sign in failed. Please check your credentials and try again.',
    errInvalidCredentials: 'Incorrect email or password.',
    errEmailTaken: 'This email address is already registered.',
    errEmailNotConfirmed: 'Please confirm your email address first.',
    errPasswordReset: 'Failed to send reset email. Please try again.',
    errPasswordUpdate: 'Failed to update password. Please try again.',
    notEnoughBots: 'Not enough bots available right now. Try again shortly.',
    botFight: 'Bot Fight',
    botFightEloLabel: 'Bot ELO bracket',
    botFightDescription: 'Watch bots battle! You are a spectator.',
    botFightStarting: 'Starting bot fight…',
    toggleOn: 'ON',
    errWatchRoom: 'Failed to watch room.',
    errReplacedByAI: 'You have been replaced by a bot of similar rank. The game will continue without you.',
    errLeaveRoom: 'Error leaving room.',
    errNotReady: 'Not all players are ready!',
    errStartGame: 'Failed to start game.',
    errNoBots: 'No available bots right now — try again later.',
    errSendMessage: 'Failed to send message',
    errLoadGlobalChat: 'Failed to load global chat history.',
    errLoadMessages: 'Failed to load message history.',
    errChatRateLimit: 'Please wait a moment before sending another message.',
    errChatRateLimitSeconds: 'Please wait {n}s before sending another message.',
    errRejoinGame: 'Failed to rejoin game.',
    rotateToPotrait: 'Please rotate your phone to portrait mode',
    connectionOfflineTitle: 'Connection lost',
    connectionOfflineBody: 'Your moves are paused until the game reconnects. Bots should not replace the host anymore.',
    connectionRecoveringTitle: 'Reconnecting…',
    connectionRecoveringBody: 'Waiting for the latest room state from the server.',
    titleYourTurn: '🔔 YOUR TURN! - Kosteczki',

    // ── Quick Play ──
    quickPlayButton: 'Quick Play',

    // ── Game Stats ──
    gameStats: 'Game Statistics',
    gameInsights: 'Game Insights',
    statLargestIsland: 'Largest island',
    statPerfect: 'Perfect',
    statMostSwaps: 'Most swaps',
    statMostOwnMarkersGained: 'Most own markers gained via swaps',
    statMostForeignTiles: 'Most foreign tiles placed',
    statTotalSwaps: 'Swaps',
    statMVP: 'MVP — biggest island',
    statGameDuration: 'Game duration',
    statMinutes: '{m}m {s}s',

    // ── UX: Highlight & Preview ──
    highlightHint: 'Available cells are highlighted',
    swapPreviewYouGive: 'You give',
    swapPreviewYouGet: 'You get',
    swapPreviewRemainingNow: 'Swaps now',
    swapPreviewRemainingAfter: 'After this swap',
    swapPreviewTargetRemaining: '{player}: swaps',
    swapConfirm: 'Confirm swap',
    swapCancel: 'Cancel',

    // ── Accessibility ──
    boardGrid: 'Game board',
    cellEmpty: 'Empty cell, row {row}, column {col}',
    cellOccupied: 'Occupied cell, row {row}, column {col}',
    markerInHand: 'Marker {index}, color {color}',

    // ── Disconnect ──
    actionsBlockedOffline: 'Actions paused — reconnecting…',

    // ── Rank Tiers ──
    rankUnranked: 'Unranked',
    rankBronze: 'Bronze',
    rankSilver: 'Silver',
    rankGold: 'Gold',
    rankPlatinum: 'Platinum',
    rankDiamond: 'Diamond',
    rankMaster: 'Master',
    rankGrandmaster: 'Grandmaster',
  },

  pl: {
    // ── General / Shared ──
    settings: 'Ustawienia',
    howToPlay: 'Jak grać',
    feedback: 'Opinie',
    logout: 'Wyloguj',
    people: 'Gracze',
    close: 'Zamknij',
    back: 'Wróć',
    join: 'Dołącz',
    leave: 'Wyjdź',
    send: 'Wyślij',
    loading: 'Ładowanie…',
    retry: 'Spróbuj ponownie',
    submitting: 'Wysyłanie…',
    yes: 'Tak',
    no: 'Nie',
    appErrorTitle: 'Coś poszło nie tak',
    appErrorBody: 'Aplikacja napotkała nieoczekiwany błąd. Możesz wrócić do lobby lub przeładować stronę.',
    appErrorBackToLobby: 'Wróć do lobby',
    appErrorReload: 'Przeładuj stronę',

    // ── Login Screen ──
    kosteczki: 'Kosteczki',
    strategyBoardGame: 'Strategiczna gra planszowa',
    online: 'online',
    loginTagline: 'Zdobywaj punkty budując największe połączone wyspy w swoim kolorze na wspólnej, wciąż rosnącej planszy.',
    featureMultiplayer: 'Gra wieloosobowa',
    featureMultiplayerDesc: 'Graj ze znajomymi w czasie rzeczywistym. Od 5 do 8 graczy na grę.',
    featureElo: 'System rankingowy ELO',
    featureEloDesc: 'Rywalizuj na globalnej tabeli wyników i zdobywaj rangi.',
    featureAI: 'Przeciwnicy AI',
    featureAIDesc: 'Zagraj solo z botami. Wszystkie wolne miejsca uzupełnimy automatycznie.',
    featureUnique: 'Każda gra jest inna',
    featureUniqueDesc: 'Wybierz algorytm generowania mapy i ciesz się unikalnym doświadczeniem za każdym razem.',
    howToPlayQuestion: 'Jak grać?',
    watchDemo: '▶ Obejrzyj demo gry',
    playGuidedDemo: '✨ Zagraj demo krok po kroku',
    watchDemoHint: 'Nie jesteś pewien? Obejrzyj powtórkę prawdziwej gry — konto nie jest wymagane.',
    signIn: 'Zaloguj się',
    createAccount: 'Utwórz konto',
    resetYourPassword: 'Zresetuj hasło',
    resetPasswordDesc: 'Wyślemy link do resetowania na Twój adres e-mail.',
    createYourAccount: 'Utwórz swoje konto',
    welcomeBack: 'Witaj ponownie',
    emailPlaceholder: 'Adres e-mail',
    passwordPlaceholder: 'Hasło',
    usernamePlaceholder: 'Nazwa użytkownika',
    sendResetEmail: 'Wyślij e-mail resetujący',
    backToSignIn: 'Powrót do logowania',
    forgotPassword: 'Zapomniałeś hasła?',
    orDivider: 'lub',
    guestPlay: 'Graj jako gość',
    guestPlayDesc: 'Wypróbuj grę bez zakładania konta. Postęp zapisany na 24h.',
    guestUsername: 'Wybierz nazwę użytkownika',
    guestStartPlaying: 'Zacznij grać',
    guestBanner: 'Konto gościa — wygasa za {time}',
    guestUpgrade: 'Zapisz swój postęp',
    guestUpgradeDesc: 'Obecnie grasz na tymczasowym koncie gościa. Załóż darmowe pełne konto, aby zachować postępy na zawsze — bez ukrytych opłat, bez płatności.',
    guestUpgradeEmail: 'Adres e-mail',
    guestUpgradePassword: 'Hasło (min. 6 znaków)',
    guestUpgradeButton: 'Załóż darmowe konto',
    guestUpgradeSuccess: 'E-mail potwierdzający wysłany! Sprawdź skrzynkę.',
    guestUpgradeConfirmed: 'Konto ulepszone! Twój postęp został zachowany.',
    guestExpired: 'Twoja sesja gościa wygasła. Utwórz konto, aby kontynuować grę.',
    errUsernameExists: 'Ta nazwa użytkownika jest już zajęta.',
    footerHobby: 'Kosteczki to darmowy projekt hobbystyczny — bez płatności, bez reklam, bez sprzedaży danych.',
    footerUpdated: 'Ostatnia aktualizacja: marzec 2026',
    footerContact: 'Kontakt:',

    // ── Reset Password ──
    setNewPassword: 'Ustaw nowe hasło',
    newPasswordDesc: 'Wprowadź nowe hasło do swojego konta.',
    newPasswordPlaceholder: 'Nowe hasło (min. 6 znaków)',
    updatePassword: 'Zaktualizuj hasło',

    // ── Verify Email ──
    confirmEmail: 'Potwierdź swój e-mail',
    confirmEmailDesc: 'Wysłaliśmy link potwierdzający na:',
    confirmEmailInstructions: 'Kliknij link w e-mailu, aby aktywować konto. Nie będziesz mieć dostępu do gry, dopóki tego nie zrobisz.',
    emailConfirmed: 'E-mail potwierdzony! Możesz się zalogować.',
    emailResent: 'E-mail potwierdzający został ponownie wysłany!',
    checkAgain: '✅ Potwierdziłem — sprawdź ponownie',
    resendEmail: '📨 Wyślij ponownie',
    logOut: '🚪 Wyloguj się',
    checkSpam: 'Nie możesz znaleźć e-maila? Sprawdź folder spam.',

    // ── Rejoin Screen ──
    welcomeBackUser: 'Witaj ponownie, {user}!',
    activeGamesToRejoin: 'Aktywne pokoje',
    roomLabel: 'Pokój: {code}',
    playersInGame: '{count} graczy w grze',
    playersInLobby: '{count}/{total} graczy w poczekalni',
    statusWaiting: 'Oczekuje',
    roomStatusWaiting: 'Oczekuje',
    roomStatusPlaying: 'W grze',
    rejoin: '↩️ Dołącz ponownie',
    returnToLobby: '↩️ Wróć do poczekalni',
    orStartNew: 'Lub rozpocznij nową grę:',
    createNewOrBrowse: '➕ Nowa gra lub przeglądaj',
    rejoinOrLeaveHint: 'Masz aktywne pokoje. Dołącz ponownie lub wyjdź z nich przed rozpoczęciem nowej gry.',

    // ── Lobby Screen ──
    welcomeUser: 'Witaj, {user}!',
    openRooms: 'Otwarte pokoje',
    watchRooms: 'Obserwuj gry',
    noRoomsAvailable: 'Brak dostępnych pokoi. Utwórz jeden, aby zacząć!',
    codeLabel: 'Kod: {code}',
    hostLabel: 'Host: {host}',
    playersSlots: '{current}/{total} graczy',
    mapLabel: 'Mapa: {algorithm}',
    spotsLeft: 'Pozostało {count} miejsc(e)',
    noWatchableRooms: 'Brak aktywnych gier do obserwowania. Sprawdź później!',
    liveBadge: 'Na żywo',
    playersCount: '{count} graczy',
    watchButton: 'Obserwuj',
    haveACode: 'Masz kod?',
    hideCodeEntry: 'Ukryj pole kodu',
    enterRoomCode: 'Wpisz kod pokoju',
    joinArrow: 'Dołącz do pokoju',
    leaderboard: 'Tabela wyników',
    leaderboardQualify: '5 gier wymaganych, aby zakwalifikować się do tabeli.',
    leaderboardPlayers: 'Gracze',
    leaderboardBots: 'Boty',
    leaderboardFriendsOnly: 'Tylko znajomi',
    leaderboardShowing: 'Pokazuje {start}-{end} z {total}',
    leaderboardPrev: 'Poprz.',
    leaderboardNext: 'Nast.',
    leaderboardNoFriends: 'Brak znajomych z kwalifikacją do rankingu',
    leaderboardTrendUp: 'Trend w górę {delta}',
    leaderboardTrendDown: 'Trend w dół {delta}',
    leaderboardTrendFlat: 'Brak ostatniej zmiany',
    leaderboardPendingBadge: 'W toku',
    noGamesPlayed: 'Brak rozegranych gier',
    needMoreGames: 'Potrzeba jeszcze {count} gier',
    createARoom: 'Utwórz pokój',
    createRoomDesc: 'Stwórz prywatny pokój i ustaw graczy, zasady oraz przebieg rozgrywki.',
    createRoomButton: 'Utwórz pokój',
    globalChatInline: 'Czat globalny',

    // ── Create Room Screen ──
    createARoomHeading: 'Utwórz pokój',
    backButton: '← Wróć',
    roomSettings: 'Ustawienia pokoju',
    numberOfPlayers: 'Liczba graczy',
    mapType: 'Typ mapy',
    corridors: 'Korytarze',
    corridorsDesc: 'Wąskie ścieżki, taktyczne blokowanie',
    compact: 'Kompaktowa',
    compactDesc: 'Kołowy wzrost, ciasne starcia',
    sprawling: 'Rozległa',
    sprawlingDesc: 'Otwarta przestrzeń, strategiczne pozycjonowanie',
    roomVisibility: 'Widoczność pokoju',
    privateRoom: 'Pokój prywatny',
    publicRoom: 'Pokój publiczny',
    privateRoomDesc: 'Ukryty w lobby — udostępnij kod, aby zaprosić graczy',
    publicRoomDesc: 'Widoczny w lobby — każdy może dołączyć',
    swapLimitPerPlayerLabel: 'Swapy na gracza',
    swapLimitPerPlayerDesc: 'Wybierz jeden preset pokoju: bez limitu albo 5 swapów na gracza.',
    swapLimitInputPlaceholder: 'Bez limitu',
    swapLimitUnlimited: 'Bez limitu',
    swapLimitPresetFive: '5 swapów/gracz',
    creatingRoom: 'Tworzenie…',
    createOnlineRoom: 'Utwórz pokój online',

    // ── Room Lobby Screen ──
    youAreHost: 'Jesteś hostem',
    hostIs: 'Host: {host}',
    privateBadge: 'Prywatny',
    copyCode: 'Kopiuj kod',
    copiedCode: 'Skopiowano!',
    playersOf: 'Gracze ({current}/{total})',
    avgElo: 'Średnie ELO: {elo}',
    eloMultiplier: 'Bonus za {count} graczy: +{bonus}% ELO',
    hostBadge: 'HOST',
    aiSuffix: '(AI)',
    botPersonaTooltip: '{persona} to baza heurystyk. Ostateczny styl zalezy tez od seeda wynikajacego z nicku bota.',
    ready: '✓ Gotowy',
    waiting: 'Oczekuje',
    emptySlot: 'Puste miejsce',
    readyButton: '✓ Gotowy',
    notReadyButton: 'Nie gotowy',
    readyCooldownHint: 'Możesz zmienić gotowość ponownie za {seconds}s.',
    fillAI: 'Dodaj boty',
    addingAI: 'Dodawanie botów…',
    startGame: 'Start',
    startingGame: 'Startowanie…',
    leaveRoom: 'Wyjdź z pokoju',
    kick: 'Wyrzuć',
    kickPlayer: 'Wyrzuć {user} z pokoju',
    kickedNotifMsg: '🚪 {user} został usunięty z pokoju',

    gameTitle: 'Kosteczki ({code})',
    stopWatching: 'Przestań oglądać',
    stopWatchingShort: 'Stop',
    sitIn: 'Wróć do gry',
    sitInNoticeTitle: 'AI przejęło Twoje miejsce',
    sitInNoticeBody: 'Twoje miejsce jest tymczasowo rozgrywane przez AI, bo byłeś nieaktywny. Każdy ruch wykonany przez AI dokłada końcową karę ELO za część gry rozegraną za Ciebie. Wróć do środka, żeby zatrzymać dalsze naliczanie.',
    sitInNoticeRatingHint: 'Na teraz AI wykonało {aiMoves} z {totalMoves} Twoich ruchów: {gainPenalty}% mniej zysku i {lossPenalty}% większa strata ELO na końcu gry.',
    sitInNoticeRatingHintIdle: 'AI nie wykonało jeszcze żadnego Twojego ruchu, więc kara jest na razie 0%. Każdy kolejny ruch AI zmniejszy zysk i zwiększy stratę ELO na końcu gry.',
    leaverLabel: 'AI przejęło',
    leaverPenaltyAria: 'Pokaż szczegóły korekty ELO za ruchy przejęte przez AI',
    leaverPenaltyTitle: 'Korekta ELO za ruchy przejęte przez AI',
    leaverPenaltyMoves: 'AI wykonało {aiMoves} z {totalMoves} ruchów za to miejsce ({percent}%).',
    leaverPenaltyLossBreakdown: '{base} pkt + {penalty}% kary AI = razem {total} pkt',
    leaverPenaltyGainBreakdown: '{base} pkt - {penalty}% zysku przez AI = razem {total} pkt',
    leaveAI: 'Wyjdź (AI przejmie)',
    leaveAIShort: 'Wyjdź',
    leaveAITooltip: 'Zostaniesz zastąpiony przez AI',
    stopWatchingTooltip: 'Przestań oglądać tę grę',
    sitInTooltip: 'Odzyskaj swoje miejsce i wróć do rozgrywki',
    yourTurn: 'Twoja tura!',
    aiPlayingYourSeatTurn: 'AI gra teraz za Ciebie',
    playerTurn: 'Tura gracza {player}',
    turnBadge: '▶ TURA',
    gameRuleColor: 'Twój kolor',
    gameRuleMode: 'Tryb',
    gameModeClassic: 'Klasyczny',
    gameModeSwapCap: '{count} swapów',
    unstuckBotButton: 'Odblokuj bota',
    unstuckBotTooltip: 'Wypisz diagnostykę gracza {player} do konsoli i wymuś ponowną próbę',
    unstuckBotDisabledTooltip: 'Dostępne tylko podczas tury bota',
    unstuckBotLabel: 'odblokuj',
    swapRemainingSelf: 'Twoje swapy',
    swapRemainingShort: 'Swapy',
    swapButton: 'ZAMIANA',
    swapHint: 'Wymień całą swoją rękę z tym graczem. Stosuj gdy mają więcej Twoich kolorów. Jeśli ostatnie N ruchów było swapem, gdzie N to liczba graczy w pokoju, następny gracz musi położyć marker.',
    swapUnavailableHint: 'Zamiana jest niedostępna w tej turze. Mogłeś wyczerpać limit swapów w pokoju albo ostatnie N ruchów było swapami, więc teraz musisz położyć marker.',
    swapEmptyPoolHint: 'Worek dobierania jest pusty, więc zamiana jest niedostępna.',
    firstMoveHint: '👆 Wybierz znacznik z ręki po prawej stronie, a następnie kliknij białe pole na planszy',
    placeMarkerHintCompact: 'Wybierz znacznik i kliknij białe pole',
    selectedMarkerLabel: 'Wybrany',
    placementPreview: 'Podgląd {row}, {col}',
    cellMarkerPickerTitle: 'Wybierz kafelek dla tego pola',
    cellMarkerPickerHint: 'Najpierw wybierz kafelek. Nadal możesz anulować przed położeniem.',
    cellMarkerPickerMarker: 'Wybierz kafelek {index}',
    cellMarkerPickerConfirm: 'Połóż tutaj',
    cellMarkerPickerCancel: 'Anuluj',
    timerExpired: 'Czas minął. AI przejmuje tę turę.',
    lastRoundBanner: '🏁 Końcówka! Zostało {count} ruchów',
    scoreGain: '+{pts} pkt',
    profileButton: 'PROFIL',
    leftToDraw: '🎒 Do dobrania',
    moveHistory: 'Historia ruchów ({count})',
    noMovesYet: 'Brak ruchów',
    movePlaced: '{player} → ({row}, {col})',
    moveSwapped: '{player} ↔ {target}',
    zoomIn: 'Powiększ',
    zoomOut: 'Pomniejsz',
    zoomReset: 'Resetuj zoom',

    // ── Game Reactions ──
    reactionNice: 'Super!',
    reactionHot: 'Gorąco!',
    reactionWow: 'Wow!',
    reactionBlock: 'Blok!',
    reactionHaha: 'Haha',
    reactionOhNo: 'O nie!',

    // ── Game End Modal ──
    gameOver: 'Koniec gry',
    gameResults: 'Wyniki gry',
    place: 'Miejsce',
    player: 'Gracz',
    pts: 'Pkt',
    elo: 'ELO',
    exAequo: 'ex aequo',
    exAequoWinner: 'Ex aequo:',
    winner: 'Zwycięzca:',
    ptsScore: '— {score} pkt',
    playersLabel: '{count} graczy',
    rematchBtn: 'Zagraj ponownie',
    rematchCreated: 'Utworzono pokoj rewanzu: {code}',
    rematchJoined: 'Dolaczono do pokoju rewanzu: {code}',
    watchReplayBtn: 'Obejrzyj powtórkę',
    backToMenu: 'Powrót do menu',
    menuLabel: 'Menu',
    durationLabel: 'Czas',

    // ── Settings Modal ──
    settingsHeading: '⚙️ Ustawienia',
    languageHeading: 'Language / Język',
    themeHeading: 'Motyw kolorystyczny',
    themeDescription: 'Przełącz tło, karty i główne akcenty w całej aplikacji.',
    themeGroupPastel: 'Pastelowe',
    themeGroupVivid: 'Wyraziste',
    themeGroupNeutral: 'Neutralne',
    themeNebula: 'Mgławica',
      themeNebulaDesc: 'Głęboka granatowo-fioletowa baza z chłodnymi niebieskimi akcentami.',
    themeEmber: 'Słoneczny Żar',
      themeEmberDesc: 'Ciemna miedź i wypalony pomarańcz z ciepłą poświatą.',
    themeLagoon: 'Laguna',
      themeLagoonDesc: 'Petrolowy błękit i teal z czystymi, wodnymi akcentami.',
    themeForest: 'Las',
      themeForestDesc: 'Sosnowa zieleń z mchowymi akcentami i spokojniejszym wykończeniem.',
    themeAurora: 'Puls zorzy',
      themeAuroraDesc: 'Ciemny teal z miętowymi i limonkowymi akcentami, żywy, ale czysty.',
    themeSunset: 'Neonowy zmierzch',
      themeSunsetDesc: 'Śliwka, koral i złoty bursztyn połączone w ciepły neonowy zmierzch.',
    themeCitrus: 'Cytrusowy zastrzyk',
      themeCitrusDesc: 'Limonkowe i mandarynkowe akcenty na ciemnej oliwkowej bazie z soczystą energią.',
    themeGraphite: 'Grafit',
      themeGraphiteDesc: 'Prawie czarna baza z dymnymi szarościami i srebrnymi akcentami.',
    themeRose: 'Różana poświata',
      themeRoseDesc: 'Głębokie jagodowe tło z miękkimi różowymi warstwami i różanymi akcentami.',
    themeDawn: 'Pastelowy świt',
      themeDawnDesc: 'Przygaszona lawenda i morela na spokojnej, wieczornoniebieskiej bazie.',
    themeSage: 'Delikatna szałwia',
      themeSageDesc: 'Stonowana eukaliptusowa zieleń i ciepły piasek dla miększego, spokojniejszego klimatu.',
    themeLilac: 'Liliowa mgła',
      themeLilacDesc: 'Przydymiona śliwka, pudrowy błękit i lawenda w delikatnym pastelowym miksie.',
    themePeach: 'Brzoskwiniowa woalka',
      themePeachDesc: 'Pudrowa brzoskwinia i mauve z miękką, wieczorną pastelową poświatą.',
    themeSeaglass: 'Morskie szkło',
      themeSeaglassDesc: 'Miękka akwamaryna i muszlowy róż w lekkim, spokojnym pastelowym wydaniu.',
    themeLaser: 'Laserowa siatka',
      themeLaserDesc: 'Elektryczny błękit i neonowa róża na głębokiej atramentowej bazie z arcade’owym pazurem.',
    themeMoonstone: 'Kamień księżycowy',
      themeMoonstoneDesc: 'Łupkowe błękitnoszarości z cichymi srebrnymi akcentami i chłodnym neutralnym klimatem.',
    themeSandstone: 'Piaskowiec',
      themeSandstoneDesc: 'Ciepłe kamienne i beżowe tony dla miękkiej neutralnej palety bez zbędnego kontrastu.',
    themeTruffle: 'Trufla',
      themeTruffleDesc: 'Stonowane kakao, taupe i szałwia dla spokojnej neutralnej palety z odrobiną ciepła.',
    accessibilityHeading: 'Dostępność i opcje wyświetlania',
    boardView: 'Widok planszy',
    view3d: '3D (Perspektywa)',
    view2d: '2D (Widok z góry)',
    viewFancy3d: 'Pełne Fancy 3D',
    view3dDesc: 'Widok 3D z efektem perspektywy — bardziej immersyjny',
    view2dDesc: 'Widok 2D z góry — lepszy dla dostępności',
    viewFancy3dDesc: 'Tryb kinowy: ocean, gęsta mgła, place budowy i rozwijające się wioski zależnie od wielkości połączonych wysp.',
    colorblindSymbols: 'Symbole dla daltonistów',
    symbolsOn: 'Symbole włączone',
    symbolsOff: 'Symbole wyłączone',
    symbolsOnDesc: 'Unikalne kształty (● ▲ ■ ◆ ★ …) wyświetlane na znacznikach i polach planszy dla daltonistów.',
    symbolsOffDesc: 'Symbole ukryte — znaczniki rozróżniane jedynie kolorem.',
    gameReactionsLabel: 'Reakcje w grze',
    reactionsOn: 'Reakcje włączone',
    reactionsOff: 'Reakcje wyłączone',
    reactionsOnDesc: 'Szybkie reakcje emoji (👍 🔥 🤯) wyświetlane podczas gier online.',
    reactionsOffDesc: 'Reakcje ukryte — brak latających emoji podczas gier.',
    chatSoundLabel: 'Dźwięki powiadomień czatu',
    chatSoundOn: 'Dźwięki włączone',
    chatSoundOff: 'Dźwięki wyłączone',
    chatSoundOnDesc: 'Odtwarzaj krótki dźwięk dla nowych ukrytych wiadomości od innych graczy.',
    chatSoundOffDesc: 'Czat pozostaje cichy — badge nieprzeczytanych nadal działa.',
    preferredColor: 'Preferowany kolor',
    preferredColorDesc: 'Wybierz preferowany kolor w grach. Jeśli jest zajęty przez innego gracza, dostaniesz następny dostępny.',
    colorN: 'Kolor {n}',
    closeSettings: 'Zamknij ustawienia',
    openSettings: 'Otwórz ustawienia',

    // ── How to Play Modal ──
    htpTitle: 'Jak grać',
    htpGoal: 'Cel',
    htpGoalText: 'Zdobądź najwięcej punktów budując duże połączone grupy (wyspy) w swoim kolorze na wspólnej planszy. Gdy wszystkie znaczniki zostaną umieszczone, gracz z najwyższym wynikiem wygrywa.',
    htpSetup: 'Przygotowanie',
    htpSetupText1: 'Każdy gracz otrzymuje unikalny kolor. Na start dostajesz 3 losowe znaczniki — mogą być w dowolnym kolorze, niekoniecznie Twoim.',
    htpSetupText2: 'Plansza zaczyna się od pojedynczego otwartego pola na środku. Pierwszy gracz musi tam umieścić znacznik (lub zamienić). Każde umieszczenie otwiera 4 bezpośrednio sąsiadujące pola, stopniowo odsłaniając mapę.',
    htpYourTurn: 'Twoja tura',
    htpYourTurnText: 'W każdej turze musisz wykonać jedną z poniższych czynności. W grach online każda tura człowieka ma limit 20 sekund.',
    htpPlaceMarker: 'A. Umieść znacznik',
    htpPlaceMarkerText: 'Dotknij / Przeciągnij (lub kliknij i upuść) dowolny znacznik z ręki na otwarte (białe) pole. Każde zamknięte pole bezpośrednio sąsiadujące z Twoim umieszczeniem zostanie otwarte. Następnie dobierasz nowy znacznik ze wspólnej puli.',
    htpSwapHands: 'B. Zamiana rąk',
    htpSwapHandsText: 'Wymień całą rękę z innym graczem. Dostępne tylko gdy pula do dobierania nie jest pusta. Pokój używa jednego z dwóch presetów swapów: bez limitu albo 5 swapów na gracza w całym meczu. Jeśli ostatnie N ruchów było swapem, gdzie N to liczba graczy w pokoju, następny gracz musi położyć marker. Użyj tego, aby zdobyć znaczniki w swoim kolorze.',
    htpExpansion: 'Rozszerzanie planszy',
    htpExpansionText: 'Gdy umieszczasz znacznik, 4 pola bezpośrednio sąsiadujące (góra, dół, lewo, prawo — nigdy po przekątnej) zmieniają się z zamkniętych na otwarte. Tak rośnie plansza.',
    htpGameStart: 'Start gry',
    htpAfterFirst: 'Po 1. ruchu',
    htpAfterSecond: 'Po 2. ruchu',
    htpExpansionNote: 'Uwaga: tylko 4 bezpośredni sąsiedzi się otwierają — przekątne pozostają zamknięte. Planuj umieszczenia, aby rozszerzać się w kierunku korzystnym dla Twoich wysp.',
    htpScoring: 'Punktacja',
    htpScoringText: 'Twój wynik to suma punktów ze wszystkich Twoich wysp. Większe wyspy są warte zdecydowanie więcej:',
    htpCells: '{n} pole/pól',
    htpScoringFormula: 'Wzór dla 4+: 7 + (rozmiar − 4) × 2 — więc 7-polowa wyspa daje 13 punktów.',
    htpExample: 'Przykład',
    htpExampleText: 'Czerwony ma dwie osobne wyspy — punktowane oddzielnie:',
    htpIslandA: 'Wyspa A (3 pola) = 4 pkt',
    htpIslandB: 'Wyspa B (2 pola) = 1 pkt',
    htpExampleNote: 'Łącznie czerwony: 4 + 1 = 5 punktów. Gdyby te 5 pól było połączonych w jedną wyspę, dałoby to 9 punktów — prawie dwa razy więcej. Łączenie pól się opłaca.',
    htpTips: 'Wskazówki',
    htpTip1: 'Rozwijaj jedną dużą wyspę zamiast wielu małych.',
    htpTip2: 'Często będziesz umieszczać znaczniki przeciwników — kładź je tam, gdzie Ci pomogą (lub im zaszkodzą).',
    htpTip3: 'Używaj zamian, aby zbierać znaczniki w swoim kolorze, gdy masz pełno cudzych.',
    htpTip4: 'Rozszerzaj planszę w kierunku korzystnym dla Twoich wysp.',
    htpGotIt: 'Rozumiem!',

    // ── Feedback Modal ──
    feedbackBoard: 'Tablica opinii',
    feedbackBrowse: 'Przeglądaj',
    feedbackNew: '+ Nowa',
    feedbackShow: 'Pokaż:',
    feedbackOpen: 'Otwarte',
    feedbackDoneClosed: 'Gotowe / Odrzucone',
    feedbackItems: '{count} pozycji',
    feedbackNoOpen: 'Brak otwartych opinii.',
    feedbackNoOpenHint: 'Nowe pomysły i zgłoszenia błędów pojawią się tutaj, gdy ktoś je doda.',
    feedbackNoDone: 'Brak gotowych lub odrzuconych opinii.',
    feedbackNoDoneHint: 'Pozycje po przeglądzie trafiają tutaj jako gotowe lub odrzucone.',
    feedbackLoadingTitle: 'Ładowanie tablicy opinii…',
    feedbackLoadingHint: 'Zbieramy właśnie otwarte pomysły, poprawki i głosy.',
    feedbackLoadError: 'Nie udało się załadować listy opinii.',
    feedbackLoadErrorHint: 'Tablica jest chwilowo niedostępna. Spróbuj ponownie za moment.',
    feedbackBug: 'Błąd',
    feedbackIdea: 'Pomysł',
    feedbackDone: 'Gotowe',
    feedbackDeclined: 'Odrzucone',
    feedbackBy: 'od {user}',
    feedbackCantVoteOwn: 'Nie możesz głosować na własną opinię',
    feedbackVoteLocked: 'Głosowanie zablokowane na zakończonych pozycjach',
    feedbackRemoveVote: 'Cofnij głos',
    feedbackUpvote: 'Zagłosuj',
    feedbackType: 'Typ',
    feedbackSuggestion: 'Sugestia',
    feedbackBugReport: 'Zgłoszenie błędu',
    feedbackDescription: 'Opis',
    feedbackBugPlaceholder: 'Opisz błąd — co się stało i co powinno się stać…',
    feedbackIdeaPlaceholder: 'Opisz swoją sugestię lub pomysł na ulepszenie…',
    feedbackCharCount: '{length}/500',
    feedbackSubmittingAs: 'Wysyłanie jako {user}',
    feedbackSubmit: 'Wyślij opinię',

    // ── People / Friends Modal ──
    peopleHeading: 'Gracze',
    tabOnline: 'Online',
    tabFriends: 'Znajomi',
    tabPending: 'Oczekujące',
    statusPlaying: 'W grze',
    statusInRoom: 'W pokoju',
    statusActive: 'Aktywny',
    statusIdle: 'Nieaktywny',
    noPlayersOnline: 'Brak graczy online',
    noPlayersOnlineHint: 'Utwórz pokój, zaproś kogoś albo zajrzyj tu ponownie za chwilę.',
    peopleOnlineLoading: 'Sprawdzamy, kto jest online…',
    peopleOnlineLoadingHint: 'Presence właśnie się synchronizuje, więc badge pokoju i gry mogą pojawić się za chwilę.',
    peopleOnlineError: 'Nie udało się załadować listy online.',
    peopleOnlineErrorHint: 'Kanał presence nie zsynchronizował się tym razem. Zamknij modal i spróbuj ponownie.',
    profileLabel: 'PROFIL',
    viewProfile: 'Zobacz profil {user}',
    moreCount: '+{count} więcej',
    noFriendsYet: 'Brak znajomych',
    noFriendsYetHint: 'Otwórz profil gracza i wyślij pierwsze zaproszenie.',
    friendsPendingOnlyHint: 'Masz już zaproszenia powyżej. Zaakceptuj jedno, aby zacząć budować listę.',
    peopleFriendsLoading: 'Ładowanie znajomych…',
    peopleFriendsLoadingHint: 'Przygotowujemy zaproszenia, akcje oglądania i statusy online.',
    peopleFriendsError: 'Nie udało się załadować listy znajomych.',
    peopleFriendsErrorHint: 'Zaproszenia do znajomych są chwilowo niedostępne. Otwórz modal ponownie za chwilę.',
    removeFriend: 'Usuń znajomego',
    noPendingRequests: 'Brak oczekujących zaproszeń',
    incomingRequests: 'Zaproszenia',
    watchGame: 'Oglądaj',
    accept: 'Akceptuj',
    decline: 'Odrzuć',
    minutesAgo: '{n} min temu',
    hoursAgo: '{n} godz. temu',
    daysAgo: '{n} dni temu',

    // ── Profile Modal ──
    noEloHistory: 'Brak historii ELO',
    profileLoading: 'Ładowanie profilu…',
    profileLoadingHint: 'Ładują się statystyki, odznaki i ostatnie powtórki.',
    errLoadProfile: 'Nie udało się załadować profilu',
    profileLoadRetryHint: 'Spróbuj ponownie za chwilę albo zamknij i otwórz modal jeszcze raz.',
    youTag: 'Ty',
    date: 'Data',
    rating: 'Ocena',
    watchReplay: 'Obejrzyj powtórkę',
    showMoreReplays: 'Pokaż więcej',
    profileRecentGamesLoading: 'Ładowanie ostatnich gier i powtórek…',
    profileRecentGamesHint: 'Akcje powtórek pojawią się tutaj, gdy historia meczów będzie gotowa.',
    profileRecentGamesError: 'Ostatnie gry są chwilowo niedostępne.',
    profileRecentGamesErrorHint: 'Profil się załadował, ale historia powtórek potrzebuje jeszcze chwili.',
    profileRecentGamesEmpty: 'Brak ostatnich powtórek.',
    profileRecentGamesEmptyHint: 'Zagraj kilka meczów online, a ta sekcja zacznie się zapełniać.',
    badgesHeading: 'Odznaki',
    badgeAlphaTester: 'Alfa Tester',
    badgeAlphaTesterDesc: 'Wczesny gracz Kosteczek',
    badgeWins: 'Zwycięzca',
    badgePerfect: 'Perfekcjonista',
    noBadges: 'Brak odznak',
    gamesToNextTier: 'gier do',
    winsToNextTier: 'wygranych do',
    perfectGamesToNextTier: 'perfekcyjnych gier do',
    tier_bronze: 'Brąz',
    tier_silver: 'Srebro',
    tier_gold: 'Złoto',
    tier_platinum: 'Platyna',
    tier_diamond: 'Diament',
    tier_master: 'Mistrz',
    aiBot: 'Bot AI',
    rankWorldwide: 'Ranga #{rank} na świecie',
    addFriend: '➕ Dodaj znajomego',
    requestSent: 'Wysłano',
    acceptFriend: '✅ Akceptuj',
    declineFriend: '✕ Odrzuć',
    friendsStatus: '✓ Znajomi',
    unfriend: 'Usuń',
    eloLabel: 'ELO',
    peak: 'Najwyższe',
    games: 'Gry',
    wins: 'Wygrane',
    losses: 'Przegrane',
    winRate: 'Wygrane %',
    goodGamesLabel: 'Dobre gry',
    eloHistory: 'Historia ELO',
    lastNGames: 'ostatnie {n} gier',
    recentGames: 'Ostatnie gry',
    botEloNote: 'Historia ELO AI pojawi się tutaj po każdej zapisanej grze.',
    noOnlineGames: 'Brak rozegranych gier online',

    // ── Replay Modal ──
    noReplayData: 'Brak danych powtórki',
    replayInitial: 'Stan początkowy — start gry',
    replayPlaced: '{player} umieścił znacznik na ({row}, {col})',
    replaySwapped: '{player} zamienił się rękami z {target}',
    demoReplayTitle: '🎬 Demo gry',
    gameReplayTitle: '🎬 Powtórka gry',
    demoReplayDesc: 'Zobacz jak wygląda prawdziwa gra — 5 graczy, strategia w akcji!',
    replayWinner: 'Zwycięzca:',
    showSymbolsCB: 'Pokaż symbole (daltonizm)',
    replayFirst: 'Pierwszy (Home)',
    replayPrev: 'Poprzedni (←)',
    replayPlayPause: 'Odtwórz/Pauza (Spacja)',
    replayNext: 'Następny (→)',
    replayLast: 'Ostatni (End)',
    demoCoachTitle: 'Szybki tutorial',
    demoCoachExplainToggle: 'Tryb wyjaśniania',
    demoCoachIntro: 'Cel: łącz swoje obszary, bo pojedyncze pola dają 0 pkt, a większe wyspy są dużo cenniejsze.',
    demoCoachStep1: 'Krok 1: Otwórz planszę od środka, żeby kolejne tury miały gdzie rosnąć.',
    demoCoachStep2: 'Krok 2: Nie stawiaj byle gdzie. Staraj się zostawiać dojścia do własnego koloru.',
    demoCoachStep3: 'Krok 3: Ten ruch wzmacnia jedną połączoną wyspę zamiast tworzyć kolejny słaby singiel.',
    demoCoachStep4: 'Krok 4: Zobacz jak ruch otwiera nowe pola wokół znacznika. Tempo też daje przewagę.',
    demoCoachStep5: 'Krok 5: Swap jest najmocniejszy wtedy, gdy inny gracz prawdopodobnie ma więcej Twojego koloru niż Ty.',
    demoCoachStep6: 'Krok 6: Zauważ, że obrona najmocniejszej wyspy bywa lepsza niż pogoń za każdym wolnym polem.',
    demoCoachStep7: 'Krok 7: Czasem najlepszy ruch przygotowuje most na 2 kolejne tury, zamiast dawać natychmiastowy zysk.',
    demoCoachStep8: 'Krok 8: Trzymaj presję na centralnych korytarzach - one decydują, kto połączy obszary później.',
    demoCoachStep9: 'Krok 9: Teraz obejrzyj resztę gry na 2x i patrz na kształt planszy, nie pojedyncze ruchy.',
    demoCoachStepMid: 'Obserwuj każdy ruch i jak zmienia się kontrola obszaru.',
    demoCoachGoal: 'Finał: wygrywa ten, kto najlepiej zamienia rozproszone pola w większe punktujące wyspy.',
    demoCoachContinue: 'Następny ruch tutoriala',
    demoCoachSkipCta: 'Ok, wiem już wszystko - chcę grać',
    demoCoachReplayFromStart: 'Obejrzyj od początku',
    demoCoachPickThenPlace: 'Najpierw wybierz marker z ręki, potem kliknij pole ({row}, {col}).',
    demoCoachClickTarget: 'Teraz Twój ruch: kliknij pole ({row}, {col}).',
    demoCoachPickMarkerFirst: 'Najpierw wybierz marker z ręki.',
    demoCoachWrongPlayerMarker: 'Wybierz marker z ręki aktywnego gracza.',
    demoCoachWrongMarker: 'Do tego ruchu wybierz podświetlony marker.',
    demoCoachWrongCell: 'Najpierw kliknij podświetlone pole, aby kontynuować tutorial.',
    demoCoachSwapAction: 'Super. Teraz wykonaj SWAP: {player} powinien zamienić rękę z {target}.',
    demoCoachSwapPickTarget: 'Wybierz cel swapa i potwierdź SWAP.',
    demoCoachSwapPickTargetFirst: 'Najpierw wybierz cel swapa.',
    demoCoachSwapPickOpponent: 'Wybierz innego gracza jako cel swapa.',
    demoCoachSwapWrongTarget: 'W tym kroku tutoriala wybierz podświetlonego gracza.',
    demoCoachSwapCta: 'Wykonaj SWAP',
    demoCoachProgress: 'Postęp tutoriala: {current}/{total}',
    demoCoachFinishCta: 'Super. Zamknij tutorial i zacznij grać',
    demoCoachSwapTitle: 'Jak działa swap',
    demoCoachSwapBody: 'Swap zamienia całą Twoją rękę z ręką wybranego gracza. Używaj go, gdy przeciwnik prawdopodobnie ma więcej znaczników w Twoim kolorze.',
    demoCoachAutoplayLocked: 'Tryb tutoriala: autoplay odblokuje się po kroku 9',
    demoCoachAutoRun2x: 'Przewodnik skończony. Reszta gry leci na 2x.',
    demoImpactPlaceTitle: 'Dlaczego ten ruch ma znaczenie',
    demoImpactPlaceChipTile: 'Marker postawiony',
    demoImpactPlaceChipOpen: '+{count} nowych pól',
    demoImpactPlaceChipIsland: 'Wyspa ma teraz: {size}',
    demoImpactPlaceChipScore: 'Przy tym rozmiarze: {points} pkt',
    demoImpactSwapTitle: 'Dlaczego ten swap ma sens',
    demoImpactSwapChipActor: 'Wykonuje: {player}',
    demoImpactSwapChipTarget: 'Cel: {target}',
    demoImpactSwapChipGoal: 'Cel: poprawić kolejne dociągi',
    demoBadgePlay: 'RUCH',
    demoBadgeOpen: 'OTWÓRZ',
    demoBadgeWinner: 'MOC',
    demoFinaleTitle: 'Jak naprawdę działa punktacja',
    demoFinaleBody: 'Pojedyncze pola są słabe. Łączenie swojego koloru w większe wyspy szybko zwiększa wynik, więc liczy się kształt planszy i moment ruchu, a nie chaotyczne rozlewanie.',
    demoFinaleLegend: 'Wyspa {size} = {points} pkt',
    demoFinalePlayerSummary: 'Największa wyspa: {largest} | Wynik: {score} | Najlepsze wyspy: {islands}',

    // ── Chat Widget ──
    globalChat: 'Czat globalny',
    chatButton: 'Czat',
    globalTab: 'Globalny',
    roomTab: 'Pokój',
    noRoomMessages: 'Brak wiadomości w pokoju. Przywitaj się!',
    noGlobalMessages: 'Brak wiadomości. Rozpocznij rozmowę!',
    messageRoom: 'Wiadomość do pokoju...',
    messageEveryone: 'Wiadomość do wszystkich...',
    chatCharCount: '{count}/{max}',
    emojiPicker: 'Wybierz emoji',

    // ── Online Players ──
    onlinePlayers: 'Gracze online',

    // ── Error / Toast Messages ──
    errPasswordMin: 'Hasło musi mieć co najmniej 6 znaków',
    errPasswordUpdated: 'Hasło zaktualizowane. Możesz się teraz zalogować.',
    errEnterEmail: 'Proszę podać adres e-mail',
    errResetSent: '✅ E-mail resetujący hasło wysłany! Sprawdź skrzynkę.',
    errEmailPassword: 'Proszę podać e-mail i hasło',
    errEnterUsername: 'Proszę podać nazwę użytkownika',
    errBotSuffix: 'Nick nie może kończyć się na "*BOT" — ta końcówka jest zarezerwowana dla botów.',
    errUsernameFormat: 'Nick musi mieć 3–20 znaków: litery, cyfry i _ (bez spacji)',
    playerRejoined: 'wrócił do gry',
    playerReplacedByAI: 'opuścił grę i został zastąpiony przez AI',
    errProfileNotFound: 'Profil nie znaleziony — spróbuj ponownie za chwilę',
    errCreateRoom: 'Nie udało się utworzyć pokoju.',
    errRoomGeneric: 'Coś poszło nie tak. Spróbuj ponownie.',
    errRoomNotFound: 'Pokój nie został znaleziony lub jest już niedostępny.',
    errRoomFull: 'Ten pokój jest już pełny.',
    errAlreadyInRoom: 'Jesteś już w innym aktywnym pokoju.',
    errHostOnly: 'Tylko gospodarz może wykonać tę akcję.',
    errNotEnoughPlayers: 'Za mało graczy, aby rozpocząć grę.',
    errGameAlreadyStarted: 'Ta gra już się rozpoczęła.',
    errKickPlayer: 'Nie udało się usunąć tego gracza.',
    errAddBot: 'Nie udało się dodać botów. Spróbuj ponownie.',
    errAuthGeneric: 'Logowanie nieudane. Sprawdź dane i spróbuj ponownie.',
    errInvalidCredentials: 'Nieprawidłowy e-mail lub hasło.',
    errEmailTaken: 'Ten adres e-mail jest już zarejestrowany.',
    errEmailNotConfirmed: 'Najpierw potwierdź swój adres e-mail.',
    errPasswordReset: 'Nie udało się wysłać e-maila resetującego. Spróbuj ponownie.',
    errPasswordUpdate: 'Nie udało się zaktualizować hasła. Spróbuj ponownie.',
    notEnoughBots: 'Zbyt mało dostępnych botów. Spróbuj za chwilę.',
    botFight: 'Walka botów',
    botFightEloLabel: 'Przedział ELO botów',
    botFightDescription: 'Oglądaj walkę botów! Jesteś widzem.',
    botFightStarting: 'Uruchamianie walki botów…',
    toggleOn: 'WŁ.',
    errWatchRoom: 'Nie udało się obserwować pokoju.',
    errReplacedByAI: 'Zostałeś zastąpiony przez bota o podobnym rankingu. Gra będzie kontynuowana bez Ciebie.',
    errLeaveRoom: 'Błąd przy wychodzeniu z pokoju.',
    errNotReady: 'Nie wszyscy gracze są gotowi!',
    errStartGame: 'Nie udało się rozpocząć gry.',
    errNoBots: 'Brak dostępnych botów — spróbuj ponownie później.',
    errSendMessage: 'Nie udało się wysłać wiadomości',
    errLoadGlobalChat: 'Nie udało się wczytać historii globalnego czatu.',
    errLoadMessages: 'Nie udało się wczytać historii wiadomości.',
    errChatRateLimit: 'Poczekaj chwilę przed wysłaniem kolejnej wiadomości.',
    errChatRateLimitSeconds: 'Poczekaj {n}s przed wysłaniem kolejnej wiadomości.',
    errRejoinGame: 'Nie udało się ponownie dołączyć do gry.',
    rotateToPotrait: 'Obróć telefon do trybu pionowego',
    connectionOfflineTitle: 'Utracono połączenie',
    connectionOfflineBody: 'Twoje ruchy są wstrzymane do czasu ponownego połączenia. Boty nie powinny już zastąpić hosta.',
    connectionRecoveringTitle: 'Ponowne łączenie…',
    connectionRecoveringBody: 'Czekamy na najnowszy stan pokoju z serwera.',
    titleYourTurn: '🔔 TWOJA TURA! - Kosteczki',

    // ── Quick Play ──
    quickPlayButton: 'Szybka gra',

    // ── Game Stats ──
    gameStats: 'Statystyki gry',
    gameInsights: 'Ciekawostki z gry',
    statLargestIsland: 'Największa wyspa',
    statPerfect: 'Perfect',
    statMostSwaps: 'Najwięcej wymian',
    statMostOwnMarkersGained: 'Najwięcej odzyskanych swoich kostek przez swapy',
    statMostForeignTiles: 'Najwięcej postawionych nie-swoich kostek',
    statTotalSwaps: 'Wymiany',
    statMVP: 'MVP — największa wyspa',
    statGameDuration: 'Czas gry',
    statMinutes: '{m}min {s}s',

    // ── UX: Highlight & Preview ──
    highlightHint: 'Dostępne pola są podświetlone',
    swapPreviewYouGive: 'Oddajesz',
    swapPreviewYouGet: 'Dostajesz',
    swapPreviewRemainingNow: 'Swapy teraz',
    swapPreviewRemainingAfter: 'Po tym swapie',
    swapPreviewTargetRemaining: '{player}: swapy',
    swapConfirm: 'Potwierdź',
    swapCancel: 'Anuluj',

    // ── Accessibility ──
    boardGrid: 'Plansza gry',
    cellEmpty: 'Puste pole, rząd {row}, kolumna {col}',
    cellOccupied: 'Zajęte pole, rząd {row}, kolumna {col}',
    markerInHand: 'Znacznik {index}, kolor {color}',

    // ── Disconnect ──
    actionsBlockedOffline: 'Akcje wstrzymane — ponowne łączenie…',

    // ── Rank Tiers ──
    rankUnranked: 'Bez rangi',
    rankBronze: 'Brąz',
    rankSilver: 'Srebro',
    rankGold: 'Złoto',
    rankPlatinum: 'Platyna',
    rankDiamond: 'Diament',
    rankMaster: 'Mistrz',
    rankGrandmaster: 'Arcymistrz',
  },
};

// ─── Context ─────────────────────────────────────────────────────────────
const I18nContext = createContext({ language: 'en', t: (key, fallback) => fallback || key, setLanguage: () => {} });

/**
 * Hook: useTranslation()
 *
 * Returns { language, t, setLanguage }
 *  - language  — current language code ('en' | 'pl')
 *  - t(key, fallback?) — returns translated string, or fallback, or key itself
 *  - setLanguage(code) — switch language
 */
export function useTranslation() {
  return useContext(I18nContext);
}

/**
 * Provider component — wrap your app with <I18nProvider language={lang} setLanguage={fn}>
 */
export function I18nProvider({ language, setLanguage, children }) {
  const t = useCallback(
    (key, fallback) => {
      const dict = translations[language] || translations.en;
      return dict[key] !== undefined ? dict[key] : (fallback !== undefined ? fallback : key);
    },
    [language]
  );

  return (
    <I18nContext.Provider value={{ language, t, setLanguage }}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Simple t() for use outside React components or where no hook is available.
 * Pass language explicitly: tFn('en', 'key')
 */
export function tFn(language, key, fallback) {
  const dict = translations[language] || translations.en;
  return dict[key] !== undefined ? dict[key] : (fallback !== undefined ? fallback : key);
}

// Available languages — used by pickers
export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski',  flag: '🇵🇱' },
];

export default translations;
